import { Loading } from '@app/react-common';
import {
  AgentConfigContact,
  ConciergeConfigContact,
  ConfigContact,
  NotificationMap,
} from '@bb/domain/dist/types/AppointmentConfig';
import { useHistory } from 'react-router-dom';
import { Listing } from '@bb/domain/dist/types/Listing';
import { IRootScopeService } from 'angular';
import React, { Fragment, useEffect } from 'react';
import swal from 'sweetalert';
import {
  applyUserDefaultContact,
  deleteUserDefaultContact,
  getFullUsers,
  saveUserDefaultContact,
} from 'api/UserController';
import type {
  IUserApplyUserDefaultContactBody,
  IUserGetFullUsersResponse,
  IUserSaveUserDefaultContactBody,
} from '@/app-package';
import { ROLES } from '@bb/users';
import {
  getListingContacts,
  saveConfigContact,
  deleteConfigContact,
  createClientContact,
  updateClientContact,
} from 'api/AppointmentConfigController';
import styled from 'styled-components';
import { ArrayValueType, formatPhoneNumber } from 'utils';
import { keyBy } from 'lodash';
import { Button, message, Modal } from 'components';
import errorReporter from '@app/errorReporter';
import { zPositions } from '@/constants';
import { UserRoles } from '@bb/users/dist/types/enums';
import {
  ContactStateType,
  PhoneType,
} from '../../../Router/ConfigContactModal/types';
import { ContactModalMode } from '../../../Router/ConfigContactModal/enums';
import { ConfigContactModalAngularComponent } from '../../../Router/ConfigContactModal/forAngular/ConfigConactModalAngularComponent';

interface AppointmentConfigContactsProps {
  contacts: ConfigContact[];
  listingId: string;
  listing: Listing;
  contactsToConfirm: boolean;
  listingAgentConfirm?: { enabled: boolean; excludeFromQueue: boolean };
  updateContacts: (contacts: ConfigContact[]) => void;
  userId: string;
  isProfile: boolean;
  answeringSubscriptionExists?: boolean;
  isMustCallDisabled: boolean;

  ModalService: any;
  $scope: ng.IScope;

  $rootScope: IRootScopeService;
}

const notificationMap = {
  Booked: 'Booked',
  Confirmed: 'Confirmed',
  Cancelled: 'Cancelled',
  Deny: 'Denied',
  'Time Change': 'Time Change',
  'Action Required': 'Reminders',
  'Feedback Collected': 'Feedback Collected',
};

const AppointmentConfigContacts: React.FC<AppointmentConfigContactsProps> = ({
  contacts,
  listingId,
  listing,
  contactsToConfirm,
  listingAgentConfirm,
  updateContacts: angularUpdateContacts,
  userId,
  isProfile,
  answeringSubscriptionExists,
  isMustCallDisabled,

  ModalService,
  $scope,

  $rootScope,
}) => {
  const updateContacts = (newContacts: ConfigContact[]) => {
    // need to manually force updates otherwise useEffect() won't trigger
    angularUpdateContacts(newContacts);
    $scope.$apply();
  };

  const history = useHistory();

  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [editingContact, setEditingContact] =
    React.useState<ContactStateType | null>(null);
  const [contactModalVisible, setContactModalVisible] =
    React.useState<boolean>(false);

  const [listingAgents, setListingAgents] = React.useState([]);
  const [listingContacts, setListingContacts] = React.useState<Record<
    string,
    ContactStateType
  > | null>({});

  const [multiConfirm, setMultiConfirm] = React.useState(false);

  const [savingContacts, setSavingContacts] = React.useState<boolean>();

  const [repsById, setRepsById] =
    React.useState<Record<string, ArrayValueType<IUserGetFullUsersResponse>>>();
  const sendFeedBackToSeller = React.useMemo(
    () =>
      listingAgents.some(({ feedbackSendToSeller }) => feedbackSendToSeller),
    [listingAgents],
  );
  const [confirmCallModalOpen, setConfirmCallModalOpen] = React.useState(false);
  const onConfirmCallModalClose = React.useRef<(confirmed: boolean) => void>();
  // init
  React.useEffect(() => {
    (async () => {
      setLoaded(false);
      setEditingContact(null);
      setContactModalVisible(false);
      setMultiConfirm(
        contacts && contacts.filter(contact => contact.mustConfirm).length > 1,
      );

      const getCurrentListingAgents = async () => {
        if (!contacts) return;
        const configAgents = contacts.filter(
          (contact: { type: string }) => contact.type === 'Agent',
        );
        const agentIdMap = keyBy(configAgents, 'contactId');

        const currentListingAgents = await getFullUsers({
          userIds: configAgents.map(
            (contact: AgentConfigContact) => contact.contactId,
          ),
        });

        setRepsById(keyBy(currentListingAgents, 'id'));
        setListingAgents(
          currentListingAgents.map(agent => ({
            ...agent,
            ...agentIdMap[agent.id],
          })),
        );
      };

      const getCurrentListingContacts = async () => {
        const currentListingContacts = await (!isProfile &&
          getListingContacts({ listingId }));
        setListingContacts(isProfile ? null : currentListingContacts);
      };

      await Promise.all([
        getCurrentListingAgents(),
        getCurrentListingContacts(),
      ]);
      setLoaded(true);
    })();
  }, [contacts]);

  // register event listener in modal service so that add contact modal can be opened from angularjs
  React.useEffect(() => {
    const onChangeListener = (isOpen: boolean) => {
      setContactModalVisible(isOpen);
    };
    if (contactModalVisible) {
      ModalService.openContactModal();
    } else {
      ModalService.closeContactModal();
    }

    ModalService.addContactModalListener(onChangeListener);
    return () => {
      ModalService.removeContactModalListener(onChangeListener);
    };
  }, [contactModalVisible]);

  useEffect(() => () => {
      swal.close();
    }, [history]);

  const applyDefaultContact = async (
    contact: IUserApplyUserDefaultContactBody,
  ) => {
    try {
      await applyUserDefaultContact(contact);
      message.success('Successfully updated all listings');
    } catch (err) {
      if (err.status >= 400)
        message[err.status >= 500 ? 'error' : 'warning'](
          err.data?.message ?? err.data,
        );
      errorReporter.error(err);
    }
  };

  const canMultiConfirm = () =>
    contactsToConfirm &&
    contacts &&
    contacts.filter(
      (contact: { type: string }) =>
        contact.type !== 'Agent' && contact.type !== 'Concierge',
    ).length > 1;

  const setupMultiConf = async () => {
    const updatedContactsPromise: Promise<ConfigContact[]> =
      ModalService.openMultiConfModal(contacts, listingContacts, listingId);
    $scope.$apply();
    const updatedContacts = await updatedContactsPromise;
    if (updatedContacts) {
      updateContacts(updatedContacts);
      const multiConfContacts = updatedContacts.filter(
        contact => contact.mustConfirm,
      );
      setMultiConfirm(multiConfContacts.length > 1);
    }
  };

  const getMultiConfIndex = ({ contactId }: { contactId: string }) => {
    const multiConfContacts = contacts
      .filter(contact => contact.mustConfirm)
      .map(contact => contact.contactId);
    return `(${multiConfContacts.indexOf(contactId) + 1} of ${
      multiConfContacts.length
    })`;
  };

  const hasListingContact = (contact: ConfigContact & { id?: string }) =>
    Object.keys(listingContacts || {}).includes(
      contact.id || contact.contactId,
    );

  const isEmpty = (obj: NotificationMap) => {
    let result = true;
    if (!obj) return result;
    Object.keys(obj).forEach((key: keyof NotificationMap) => {
      if (obj[key]) result = false;
    });
    return result;
  };

  const closeContactModal = () => {
    setContactModalVisible(false);
  };

  const openAddContactModal = () => {
    setContactModalVisible(true);
    setEditingContact(null);
  };

  const openEditContactModal = (contact: ContactStateType) => {
    setContactModalVisible(true);

    const newEditingContact: Omit<ContactStateType, 'phone'> & {
      phone: (
        | {
            mustCall: boolean;
            number: string;
            sendSms: boolean;
            type: PhoneType;
          }
        | Record<string, never>
      )[];
    } = { ...contact };
    if (contact.type === 'Agent') {
      newEditingContact.rep = newEditingContact.contactId;
    } else {
      const fullContact = listingContacts[newEditingContact.contactId];
      if (!fullContact) {
        message.error('Could not edit this contact');
        return;
      }
      newEditingContact.deliveries = fullContact.deliveries;
      newEditingContact.firstName = fullContact.firstName;
      newEditingContact.lastName = fullContact.lastName;
      newEditingContact.email = fullContact.email;
      newEditingContact.phone = fullContact.phone.length
        ? [...fullContact.phone]
        : [{}];
      if (contact.type === 'Concierge') {
        newEditingContact.holdAppointments = fullContact.holdAppointments;
      }
    }
    setEditingContact(newEditingContact as ContactStateType);
  };

  const onContactSave = async (
    contact: ContactStateType,
    contactModalMode: ContactModalMode,
    shouldClone: boolean,
    isEditMode?: boolean,
  ) => {
    if (
      (answeringSubscriptionExists === false ||
        listing.useAppointmentDesk === false) &&
      contact.phone.some(phone => phone.number && phone.mustCall) &&
      (listing.repsObjectIds.includes(($rootScope as any).auth.id) ||
        ROLES[($rootScope as any).auth.role as UserRoles] >=
          ROLES.GLOBAL_ADMIN) &&
      listing.country === 'USA'
    ) {
      const waitConfirm = new Promise<boolean>(resolve => {
        onConfirmCallModalClose.current = (confirmed: boolean) =>
          resolve(confirmed);
      });
      setConfirmCallModalOpen(true);
      const confirmed = await waitConfirm;
      if (!confirmed) return;
    }
    setSavingContacts(true);
    const updatedContact: ContactStateType & {
      userId?: string;
      listingId?: string;
    } = { ...contact };

    // filter non empty phones
    if (updatedContact.type !== 'Agent') {
      updatedContact.phone = updatedContact.phone.filter(p => !!p.number);
    }
    if (isProfile) {
      updatedContact.userId = userId;
      const updatedContacts: ConfigContact[] = await saveUserDefaultContact(
        { userId: updatedContact.userId },
        updatedContact as IUserSaveUserDefaultContactBody,
      );
      setContactModalVisible(false);
      setSavingContacts(false);
      if (updatedContacts) {
        updateContacts(updatedContacts);
        swal(
          {
            title: 'Edit Contact',
            text: 'Apply this change to all existing listings?',
            html: true,
            type: 'info',
            showCancelButton: true,
            confirmButtonColor: '#3ea800',
            confirmButtonText: 'Apply',
            cancelButtonText: 'Cancel',
            closeOnConfirm: true,
          },
          () => {
            applyDefaultContact({
              isEdit: isEditMode,
              ...updatedContact,
            } as IUserApplyUserDefaultContactBody);
          },
        );
      }
    } else if (
      updatedContact.type === 'Concierge' ||
      updatedContact.type === 'Agent'
    ) {
      updatedContact.listingId = listingId;
      const result: { updatedContacts: ConfigContact[] } =
        await saveConfigContact(
          {
            listingId: updatedContact.listingId,
            contactId: updatedContact.contactId,
          },
          updatedContact as ConciergeConfigContact | AgentConfigContact,
        );
      const { updatedContacts } = result;

      setContactModalVisible(false);
      setSavingContacts(false);
      if (updatedContacts) {
        updateContacts(updatedContacts);
      }
    } else {
      const { contactId } = updatedContact;
      const contactBody = {
        type: updatedContact.type,
        firstName: updatedContact.firstName,
        lastName: updatedContact.lastName,
        email: updatedContact.email,
        phone: updatedContact.phone,
        pronoun: updatedContact.pronoun,
      };
      const contactConfig = {
        importance: updatedContact.importance,
        deliveries: updatedContact.deliveries,
        phone: updatedContact.phone,
        notifications: updatedContact.notifications,
        canConfirmAppts: updatedContact.canConfirmAppts,
        canDenyAppts: updatedContact.canDenyAppts,
        canSuggestTime: updatedContact.canSuggestTime,
        mustConfirm: updatedContact.mustConfirm,
      };

      let promise;
      if (
        contactModalMode === 'ADD_NEW' ||
        (contactModalMode === 'ADD_EXISTING' && shouldClone)
      ) {
        promise = createClientContact(
          { listingId },
          { contactBody, contactConfig },
        );
      } else if (
        contactModalMode === 'ADD_EXISTING' ||
        contactModalMode === 'ADD_EXISTING_EDITED' ||
        contactModalMode === 'EDIT_ALL'
      ) {
        promise = updateClientContact(
          { contactId, listingId },
          { contactBody, contactConfig },
        );
      } else {
        promise = updateClientContact(
          { contactId, listingId },
          { contactBody, contactConfig, updateConfigOnly: true },
        );
      }

      try {
        const { updatedContacts }: { updatedContacts: ConfigContact[] } =
          await promise;
        setContactModalVisible(false);
        if (updatedContacts) {
          updateContacts(updatedContacts);
        }
      } catch (err) {
        if (err.response?.status === 400 && err.response.data?.message) {
          message.error(err.response.data.message);
        }
        if (err.status >= 400)
          message[err.status >= 500 ? 'error' : 'warning'](
            err.data?.message ?? err.data,
          );
        errorReporter.error(err);
      } finally {
        setSavingContacts(false);
      }
    }
  };

  const deleteContact = async (contact: ConfigContact) => {
    // Remove contact from appointmentConfig;
    let name;
    if (contact.type === 'Agent') {
      const contactDetails = repsById[contact.contactId];
      ({ name } = contactDetails);
    } else {
      const contactDetails = listingContacts[contact.contactId];
      if (contactDetails) {
        name = `${contactDetails.firstName} ${contactDetails.lastName}`;
      }
    }

    swal(
      {
        title: 'Delete Contact',
        text: `Are you sure you want to delete ${name}?`,
        html: true,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#cb4800',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        closeOnConfirm: !isProfile,
      },
      async () => {
        if (isProfile) {
          swal(
            {
              title: 'Delete Contact',
              text: 'Apply this change to all existing listings?',
              html: true,
              type: 'info',
              showCancelButton: true,
              confirmButtonColor: '#3ea800',
              confirmButtonText: 'Apply',
              cancelButtonText: 'Cancel',
              closeOnConfirm: true,
            },
            async confirm => {
              setLoaded(false);

              try {
                const {
                  updatedContacts,
                }: { updatedContacts: ConfigContact[] } =
                  await deleteUserDefaultContact({
                    userId,
                    contactId: contact.contactId,
                  });

                updateContacts(updatedContacts);
                if (confirm) {
                  applyDefaultContact({
                    userId,
                    contactId: contact.contactId,
                    isDelete: true,
                  });
                }
              } finally {
                setLoaded(true);
              }
            },
          );
        } else {
          const { updatedContacts }: { updatedContacts: ConfigContact[] } =
            await deleteConfigContact({
              listingId,
              contactId: contact.contactId,
            });

          updateContacts(updatedContacts);
        }
      },
    );
  };

  const getContactEntry = (
    contact: any,
    index: number,
    isListingAgent: boolean,
  ) => (
      <Fragment key={index}>
        <tr>
          <td>
            <span className="font-bold">
              {isListingAgent
                ? contact.name
                : `${listingContacts[contact.contactId].firstName} ${
                    listingContacts[contact.contactId].lastName
                  }`}
            </span>
            <br />
            <small>
              {isListingAgent
                ? `Agent - ${contact.importance}`
                : `${contact.type} - ${contact.importance}`}
            </small>
            {!isListingAgent && (
              <>
                <br />
                {multiConfirm && contact.mustConfirm && (
                  <span className="text-primary-2">
                    <i className="pe-7s-attention fat"></i>
                    <small>
                      Multi-conf required
                      <br />
                      {getMultiConfIndex(contact)}
                    </small>
                  </span>
                )}
              </>
            )}
          </td>

          <td>
            {isListingAgent
              ? contact.email
              : listingContacts[contact.contactId].email}
            {(isListingAgent
              ? contact.officeMessages.appointmentUpdates.email
              : listingContacts[contact.contactId].deliveries.Email) && (
              <StyledDeliveryText>RECEIVING EMAILS</StyledDeliveryText>
            )}
          </td>

          <td>
            {isListingAgent ? (
              <>
                {formatPhoneNumber(contact.phone)}
                {contact.officeMessages.appointmentUpdates.text && (
                  <StyledDeliveryText>RECEIVING TEXTS</StyledDeliveryText>
                )}
              </>
            ) : (
              <>
                {listingContacts[contact.contactId].phone.map(
                  (
                    phoneInfo: {
                      mustCall: boolean;
                      number: string;
                      sendSms: boolean;
                      type: PhoneType;
                    },
                    phoneIndex: number,
                  ) => (
                    <div key={phoneIndex} className="no-padding">
                      <h6 className="font-bold">{phoneInfo.type}:</h6>
                      <h6>{formatPhoneNumber(phoneInfo.number)}</h6>
                      {phoneInfo.sendSms && (
                        <StyledDeliveryText>RECEIVING TEXTS</StyledDeliveryText>
                      )}
                      {phoneInfo.mustCall && !isMustCallDisabled && (
                        <StyledDeliveryText color="red">
                          MUST CALL
                        </StyledDeliveryText>
                      )}
                      <br />
                    </div>
                  ),
                )}
              </>
            )}
          </td>

          <td>
            <span className="font-bold font-uppercase">
              Confirm -{' '}
              <StyledPermissionText
                color={contact.canConfirmAppts ? 'green' : 'red'}
              >
                {contact.canConfirmAppts ? 'Yes' : 'No'}
              </StyledPermissionText>
              <br />
              Deny -{' '}
              <StyledPermissionText
                color={contact.canDenyAppts ? 'green' : 'red'}
              >
                {contact.canDenyAppts ? 'Yes' : 'No'}
              </StyledPermissionText>
            </span>
            {contact.canSuggestTime && (
              <StyledDeliveryText>CAN SOFT SUGGEST TIME</StyledDeliveryText>
            )}
          </td>

          <td>
            {Object.keys(contact.notifications).map(
              (ind: keyof NotificationMap) =>
                contact.notifications[ind] && (
                  <div key={ind}>{notificationMap[ind]}</div>
                ),
            )}
            {isEmpty(contact.notifications) && <div>None</div>}
          </td>

          <td>
            <a
              onClick={() => openEditContactModal(contact)}
              data-cy={`edit-contact-${index}`}
            >
              Edit
            </a>
            |
            <a
              onClick={() => deleteContact(contact)}
              data-cy={`delete-contact-${index}`}
            >
              Delete
            </a>
          </td>
        </tr>
      </Fragment>
    );

  return (
    <>
      {!loaded ? (
        <Loading size="fa-4x" />
      ) : (
        <>
          <fieldset>
            <StyledLegend>
              <span>{isProfile ? '' : 'Manage Listing Contacts'}</span>
              <div>
                {canMultiConfirm() && (
                  <button
                    className={`btn ${
                      multiConfirm ? 'btn-primary2' : 'btn-info'
                    }`}
                    onClick={setupMultiConf}
                    data-cy="multi-confrm"
                  >
                    {` ${multiConfirm ? 'Manage' : 'Setup'} Multiple Conf `}
                  </button>
                )}{' '}
                <button
                  className="btn btn-primary"
                  onClick={openAddContactModal}
                  data-toggle="modal"
                  data-target="#contactModal"
                  data-cy="add-contact"
                >
                  {' Add Contact '}
                </button>
              </div>
            </StyledLegend>

            {contacts.length === 0 ? (
              <h4>No {isProfile ? 'default' : 'listing'} contacts defined.</h4>
            ) : (
              <table className="table table-striped table-bordered table-hover">
                <tbody>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>
                      Device
                      <br />
                      Permissions
                    </th>
                    <th>Subscribed</th>
                    <th></th>
                  </tr>

                  {listingAgents
                    .filter(contact => isProfile || hasListingContact(contact))
                    .map((contact, index) =>
                      getContactEntry(contact, index, true),
                    )}

                  {contacts
                    .filter(
                      contact =>
                        contact.type !== 'Agent' &&
                        !isProfile &&
                        hasListingContact(contact),
                    )
                    .map((contact, index) =>
                      getContactEntry(contact, index, false),
                    )}
                </tbody>
              </table>
            )}
          </fieldset>
          <ConfigContactModalAngularComponent
            contact={editingContact}
            contacts={contacts}
            isProfile={isProfile}
            isSaving={savingContacts}
            listingId={listingId}
            listing={listing}
            visible={contactModalVisible}
            onClose={closeContactModal}
            onSave={onContactSave}
            userId={userId}
            $rootScope={$rootScope}
            feedbackSendToSeller={sendFeedBackToSeller}
            isMustCallDisabled={isMustCallDisabled}
          />
          <Modal
            title="Must Call"
            open={confirmCallModalOpen}
            onCancel={() => {
              setConfirmCallModalOpen(false);
              onConfirmCallModalClose.current?.(false);
            }}
            footer={
              <div>
                <Button
                  type="primary"
                  onClick={() => {
                    setConfirmCallModalOpen(false);
                    onConfirmCallModalClose.current?.(true);
                  }}
                >
                  I Acknowledge
                </Button>
              </div>
            }
            zIndex={zPositions.contactModalComponents}
          >
            You have indicated that a listing contact requires a physical phone
            call to confirm this showing. You do not have Live Concierge set up
            and therefore cannot perform this action. Upon clicking Save, you
            will be redirected to the Live Concierge service page to sign up for
            the subscription.
          </Modal>
        </>
      )}
    </>
  );
};

export default AppointmentConfigContacts;

const StyledLegend = styled.legend`
  &&& {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const StyledDeliveryText = styled.h6`
  &&& {
    font-size: 10px;
    color: ${props => props.color ?? 'green'};
  }
`;

const StyledPermissionText = styled.span`
  &&& {
    color: ${props => props.color ?? 'green'};
  }
`;
